import { useBillingSubscription, useBillingUsage } from '../../api/queries.js';
import { useAuthStore } from '../../auth/auth-store.js';
import { Button, MeterBar, PageHeader, Toggle, formatDate } from '../../components/ui/index.js';

const PLAN_NAMES: Record<string, string> = {
  free: 'Free',
  starter: 'Starter',
  business: 'Business',
  enterprise: 'Enterprise',
};

export function SettingsBilling(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const slug = user?.workspaceSlug;
  const subQ = useBillingSubscription(slug);
  const usageQ = useBillingUsage(slug);

  const sub = subQ.data;
  const u = usageQ.data ?? {
    tickets_used: 14382,
    tickets_limit: 20000,
    operators_used: 7,
    operators_limit: 20,
    storage_used_bytes: 3.2 * 1024 ** 3,
    storage_limit_bytes: 10 * 1024 ** 3,
    data_connectors_used: 2,
    data_connectors_limit: 3,
  };

  return (
    <div className="inbx-page">
      <PageHeader title="Биллинг и тарифы" />
      <div className="inbx-page__body" style={{ maxWidth: 880 }}>
        <div className="inbx-card">
          <h4 className="inbx-card__title">Текущий план</h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 'var(--inbx-fz-2xl)', fontWeight: 600 }}>
                {PLAN_NAMES[sub?.plan ?? 'business']}
              </div>
              <div style={{ color: 'var(--inbx-text-tertiary)' }}>
                {sub?.status === 'trialing'
                  ? 'Пробный период до ' + formatDate(sub.trial_ends_at)
                  : sub?.current_period_end
                    ? `Следующее списание: ${formatDate(sub.current_period_end)}`
                    : '—'}
              </div>
            </div>
            <Button variant="primary">Изменить план</Button>
            <Button variant="ghost">Отменить подписку</Button>
          </div>
        </div>

        <div className="inbx-card">
          <h4 className="inbx-card__title">Использование лимитов</h4>
          <Limit label="Тикеты в этом месяце" used={u.tickets_used} limit={u.tickets_limit} />
          <Limit label="Операторы" used={u.operators_used} limit={u.operators_limit} />
          <Limit
            label="Хранилище"
            used={Math.round((u.storage_used_bytes / 1024 ** 3) * 10) / 10}
            limit={Math.round(u.storage_limit_bytes / 1024 ** 3)}
            unit="GB"
          />
          <Limit
            label="Коннекторы данных"
            used={u.data_connectors_used}
            limit={u.data_connectors_limit}
          />
        </div>

        <div className="inbx-card">
          <h4 className="inbx-card__title">Поведение при превышении</h4>
          <Toggle
            checked={true}
            onChange={() => {}}
            label="Разрешить овераж (превышение лимитов с доначислением)"
          />
        </div>

        <div className="inbx-card">
          <h4 className="inbx-card__title">Способ оплаты</h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <i className="ti-credit-card" style={{ fontSize: 28 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500 }}>•••• 4242</div>
              <div style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}>
                Истекает 12/27
              </div>
            </div>
            <Button variant="secondary">Изменить</Button>
          </div>
        </div>

        <div className="inbx-card">
          <h4 className="inbx-card__title">История платежей</h4>
          <table className="inbx-table">
            <thead>
              <tr>
                <th>Дата</th>
                <th>Описание</th>
                <th style={{ textAlign: 'right' }}>Сумма</th>
                <th>Статус</th>
                <th>Документ</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>01.05.2026</td>
                <td>Business · 7 операторов</td>
                <td style={{ textAlign: 'right' }}>23 920 ₽</td>
                <td>
                  <span className="inbx-pill-badge inbx-pill-badge--success">Оплачен</span>
                </td>
                <td>
                  <Button size="sm" variant="ghost" icon="ti-download">
                    PDF
                  </Button>
                </td>
              </tr>
              <tr>
                <td>01.04.2026</td>
                <td>Business · 7 операторов + овераж</td>
                <td style={{ textAlign: 'right' }}>26 111 ₽</td>
                <td>
                  <span className="inbx-pill-badge inbx-pill-badge--success">Оплачен</span>
                </td>
                <td>
                  <Button size="sm" variant="ghost" icon="ti-download">
                    PDF
                  </Button>
                </td>
              </tr>
              <tr>
                <td>01.03.2026</td>
                <td>Business · 6 операторов</td>
                <td style={{ textAlign: 'right' }}>20 940 ₽</td>
                <td>
                  <span className="inbx-pill-badge inbx-pill-badge--success">Оплачен</span>
                </td>
                <td>
                  <Button size="sm" variant="ghost" icon="ti-download">
                    PDF
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Limit({
  label,
  used,
  limit,
  unit,
}: {
  label: string;
  used: number;
  limit: number;
  unit?: string;
}): JSX.Element {
  const pct = Math.min(100, Math.round((used / limit) * 100));
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 'var(--inbx-fz-sm)',
          marginBottom: 4,
        }}
      >
        <span>{label}</span>
        <span style={{ color: 'var(--inbx-text-tertiary)' }}>
          {used} {unit ?? ''} / {limit} {unit ?? ''} · {pct}%
        </span>
      </div>
      <MeterBar pct={pct} />
    </div>
  );
}
